import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type Resolver, type SubmitHandler } from 'react-hook-form';
import {
    editableContactFieldsSchema,
    editablePendingCustomerSchema,
    type EditPendingCustomerFormInputs,
} from '../../schemas/customer.schemas';
import { computeCustomerDiff, applyCustomerDiff, type DiffEntry } from './computeCustomerDiff';
import { EditCustomerConfirmModal } from './EditCustomerConfirmModal';
import { CustomerNameFields } from '../fields/CustomerNameFields';
import { CustomerAddressFields } from '../fields/CustomerAddressFields';
import { CustomerContactFields } from '../fields/CustomerContactFields';
import { CustomerCompanyFields } from '../fields/CustomerCompanyFields';
import { Form, Button } from '@shared/components';
import type { Customer } from '../../types/customers.types';

interface EditCustomerFormProps {
    customer: Customer;
    onCancel: () => void;
    onSaved: (updated: Customer) => void;
    /**
     * Persists the merged edit. Both modes (existing/pending customer) merge
     * the edit into the quote's selectedCustomer and save the quote via this
     * callback — neither mode calls a customer API. Supplied by the quote
     * journey (CustomerCard -> CustomerSelectionStep -> the wizard's single
     * saveQuote instance). If it is ever absent, handleConfirm fails loudly
     * instead of silently no-opping (#664).
     */
    onSavePending?: (updated: Customer) => Promise<void>;
}

function toDefaultValues(c: Customer): EditPendingCustomerFormInputs {
    return {
        title: c.title,
        forename: c.forename,
        surname: c.surname,
        addressLine1: c.address_line_1 ?? '',
        addressLine2: c.address_line_2 ?? '',
        city: c.city ?? '',
        postcode: c.postcode ?? '',
        email: c.email ?? '',
        mobilePhone: c.mobile_phone ?? '',
        businessName: c.business_name ?? '',
        vatNumber: c.vat_number ?? '',
    };
}

export const EditCustomerForm: React.FC<EditCustomerFormProps> = ({
    customer,
    onCancel,
    onSaved,
    onSavePending,
}) => {
    // Mode B (pending, no id): all fields editable, incl. name/title.
    // Mode A (existing, id present): address/contact only, name read-only.
    const isPending = !customer.id;

    const [isConfirming, setIsConfirming] = useState(false);
    const [pendingDiff, setPendingDiff] = useState<DiffEntry[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<EditPendingCustomerFormInputs>({
        // Mode A's schema (editableContactFieldsSchema) omits title/forename/
        // surname, so zod strips them from the resolver-parsed submit values —
        // this is how Mode A's diff never sees name keys (see the
        // "!(camelKey in proposed)" guard in computeCustomerDiff). The cast is
        // needed because the two schemas' output types differ by design.
        resolver: zodResolver(
            isPending ? editablePendingCustomerSchema : editableContactFieldsSchema,
        ) as unknown as Resolver<EditPendingCustomerFormInputs>,
        defaultValues: toDefaultValues(customer),
        mode: 'onBlur',
    });

    const handleReviewChanges: SubmitHandler<EditPendingCustomerFormInputs> = (values) => {
        const diff = computeCustomerDiff(customer, values);
        if (diff.length === 0) {
            onCancel();
            return;
        }
        setPendingDiff(diff);
        setIsConfirming(true);
    };

    const handleConfirm = async () => {
        if (!onSavePending) {
            // #664 fallback: fail loudly, never silently. Should not occur —
            // the quote step always supplies onSavePending.
            setSaveError('This customer cannot be saved from here.');
            return;
        }
        setSaveError(null);
        setIsSaving(true);
        try {
            // applyCustomerDiff spreads the original, so customer.id (when
            // present) and any unedited fields are preserved. Mode A: name
            // untouched. Mode B: name may change.
            const merged = applyCustomerDiff(customer, pendingDiff);
            await onSavePending(merged);
            setIsConfirming(false);
            onSaved(merged);
        } catch {
            // The save lane surfaces the failure. Keep the form open so the agent can retry or cancel.
            // Do NOT call onSaved; no customer API is touched.
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelConfirm = () => {
        // While a save is in flight the confirm modal's buttons are disabled,
        // but the shared Modal still routes Escape, overlay-click, and the
        // close icon through onClose (this handler). Guard here so those
        // dismissal paths match the disabled-button semantics and the modal
        // cannot be closed mid-save (#664 review).
        if (isSaving) return;
        setIsConfirming(false);
    };

    return (
        <>
            <Form onSubmit={handleSubmit(handleReviewChanges)} className="space-y-4 p-4 rounded-lg border border-mp-border">
                {isPending ? (
                    <CustomerNameFields control={control} />
                ) : (
                    <div className="flex items-baseline gap-2">
                        <h3 className="text-lg font-semibold">
                            {customer.title} {customer.forename} {customer.surname}
                        </h3>
                        <span
                            className="text-xs text-mp-muted"
                            title="Name changes are handled separately. Contact support for name corrections."
                        >
                            (name read-only)
                        </span>
                    </div>
                )}

                <CustomerAddressFields control={control} />
                <CustomerContactFields control={control} />
                <CustomerCompanyFields control={control} />

                {saveError && (
                    <p className="text-sm text-mp-error" role="alert">
                        {saveError}
                    </p>
                )}

                <div className="flex justify-end gap-2 mt-4">
                    <Button onClick={onCancel} variant="clear" type="button">
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        disabled={isSubmitting || Object.keys(errors).length > 0}
                    >
                        Review changes
                    </Button>
                </div>
            </Form>

            <EditCustomerConfirmModal
                isOpen={isConfirming}
                diff={pendingDiff}
                isSubmitting={isSaving}
                onCancel={handleCancelConfirm}
                onConfirm={handleConfirm}
            />
        </>
    );
};
